<template>
  <main class="flex flex-col items-center">
    <section class="custom-container-forum max-w-[1344px] w-full">
      <BannerForum
        :isTeacherOrAdmin="isTeacherOrAdmin()"
        :content="bannerContent"
      />
      <div class="md:flex md:mt-[46px] md:gap-8">
        <div
          class="card md:h-[289px] w-full md:w-[21vw] min-w-[250px] md:min-w-[311px]"
        >
          <ExploreTopics
            @topicSelected="exploreTopicEmit"
            :topicExplored="topicExplored"
          />
        </div>
        <div class="w-full">
          <div class="w-full">
            <HeaderTopics
              :searchInput="searchInput"
              :totalTopics="totalTopics"
              @onSearchTopics="searchTopics"
              @onOrderFilter="filterByOrder"
              :topicExplored="topicExplored"
            />
          </div>
          <div class="w-full mt-10">
            <ListTable
              :topics="topics"
              :totalTopics="totalTopics"
              :query="query"
              :numberOfTopicsPerPage="numberOfTopicsPerPage"
              @paginate="changePage"
              @clearFilter="clearFilter"
            />
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script lang="ts">
import { defineAsyncComponent, defineComponent } from "vue";
import { ForumService } from "../forum.service";
import { QueryParams } from "@/models/query-params.model";
import { Topic } from "@/models/topic.model";
import { useRoute } from "vue-router";
import { getUserFromStorage } from "@/utils/user-store.util";
import { Roles } from "@/models/user.model";
import { take } from "rxjs";
import { useCourseStudentStore } from "@/store/course-student";

const BannerForum = defineAsyncComponent(
  () => import("../components/BannerForum/banner-forum.vue")
);
const HeaderTopics = defineAsyncComponent(
  () => import("./Header/header-topics.vue")
);
const ListTable = defineAsyncComponent(
  () => import("./ListTable/list-table.vue")
);
const ExploreTopics = defineAsyncComponent(
  () => import("./ExploreTopics/explore-topics.vue")
);

export default defineComponent({
  name: "forum",
  components: {
    HeaderTopics,
    ListTable,
    ExploreTopics,
    BannerForum,
  },

  data() {
    return {
      topics: [] as Array<Topic>,
      totalTopics: 0,
      numberOfTopicsPerPage: 0,
      query: new QueryParams(),
      route: useRoute(),
      searchInput: "",
      topicExplored: {
        label: this.$t("labels.generic", { pt: "Todos", en: "All" }),
        query: {},
        message: "",
      },
      bannerContent: {
        imageBanner: "",
        nameBanner: "",
      },
    };
  },
  mounted() {
    this.getRouteParams();
    this.loadInitialQuery();
    this.getAllTopics();
    this.setBannerByUserRole();
  },
  methods: {
    getAllTopics(): void {
      this.service.topics.pipe().subscribe({
        next: (response) => {
          this.topics = response.content;
          this.totalTopics = response.total;
          this.numberOfTopicsPerPage = response.size;
        },
      });

      this.query.courseId = this.$route.params.id as string;

      this.service.getAllTopics(this.query);
    },
    searchTopics(searchQuery: string): void {
      this.query.title = searchQuery;
      this.searchInput = searchQuery;
      this.query.page = 0;

      const updatedQuery = {
        ...this.route.query,
        page: 0,
        title: searchQuery,
      };

      this.$router.push({ name: "forum", query: updatedQuery });
      this.service.getAllTopics(this.query);
    },
    getRouteParams(): void {
      if (this.$route.query) {
        this.query = {
          page: Number(this.$route.query.page) || 0,
          size: this.numberOfTopicsPerPage || 10,
          title: (this.$route.query.title as string) || "",
          orderDir: (this.$route.query.orderDir as string) || "",
        };
      }
    },
    filterByOrder(order: string): void {
      this.query = { ...this.query, orderDir: order };
      this.$router.push({
        name: "forum",
        query: { ...this.$route.query, orderDir: order },
      });
      this.getAllTopics();
    },
    changePage(query: QueryParams): void {
      this.query = { ...this.query, page: query.page, size: query.size };
      this.$router.push({
        name: "forum",
        query: { ...this.$route.query, page: query.page, size: query.size },
      });
      this.getAllTopics();
    },
    clearFilter(query: QueryParams): void {
      this.query = { ...this.query, ...query };
      this.searchInput = "";
      this.$router.push({
        name: "forum",
        query: {
          ...this.$route.query,
          page: 0,
          title: this.query.title,
          status: this.query.status,
        },
      });
      this.getAllTopics();
    },
    exploreTopicEmit(event): void {
      this.query.status = event.value.query.status;
      this.topicExplored = event.value;
      this.$router.push({
        name: "forum",
        query: {
          ...this.$route.query,
          status: this.query.status,
          orderDir: this.query.orderDir,
        },
      });
      // localStorage.setItem("course-type", JSON.stringify(course?.type));
      // useCourseStudentStore.setCourseType(course?.type!);
      this.getAllTopics();
    },
    loadInitialQuery(): void {
      if (this.$route.query) {
        this.query = { ...this.query, ...this.$route.query };
      }
    },
    setBannerByUserRole(): void {
      if (this.isTeacherOrAdmin()) {
        this.getCourseById();
      } else {
        this.bannerContent.imageBanner = require("@/assets/images/forum-banner.svg");
        this.bannerContent.nameBanner = this.$t("forum.banner.title");
      }
    },
    isTeacherOrAdmin(): boolean {
      const userRole = getUserFromStorage()?.role;
      return userRole === Roles.ADMINISTRATOR || userRole === Roles.TEACHER;
    },
    getCourseById(): void {
      this.service.course.pipe(take(1)).subscribe({
        next: (response) => {
          this.bannerContent.imageBanner = response.file.url;
          this.bannerContent.nameBanner =
            this.$t("forum.banner.title") + " - " + response.name;
        },
      });
      this.service.getCourseById(this.route.params.id as string);
    },
  },
  computed: {
    service(): ForumService {
      return new ForumService();
    },
  },
});
</script>
